import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import configureStore from 'state/store';
import LogoutTimer from 'utils/LogoutTimer';
import introspectionQueryResultData from 'graphql/schema/introspectionQueryResult';
import schema from 'graphql/schema/schema.json';
import Cookies from 'universal-cookie';
import { uuIdGenerator } from 'utils/UuIdGenerator';
import { serviceActions } from './state/modules/service';
import { AlertType } from './graphql/schema/p4types';
import { RetryLink } from '@apollo/client/link/retry';
import { buildTypePolicies } from './utils/typePolicies';
import { add } from 'date-fns';

/**
 * Extracts `PossibleTypesMap` as accepted by `@apollo/client` from GraphQL introspection query result
 * (From https://github.com/apollographql/apollo-client/issues/6855)
 */
const introspectionToPossibleTypes = (introspectionQueryResultData) => {
  const possibleTypes = {};

  introspectionQueryResultData.__schema.types.forEach((supertype) => {
    if (supertype.possibleTypes) {
      possibleTypes[supertype.name] = supertype.possibleTypes.map((subtype) => subtype.name);
    }
  });

  return possibleTypes;
};

const ignoredOperation = (operationName) => operationName === 'SwitchUser' || operationName === 'SpoofUser';

export const store = configureStore();
const logoutTimer = new LogoutTimer(store);

const onErrorLink = (props) => {
  const { graphQLErrors, networkError, operation } = props;

  const statusCode = networkError.statusCode;
  if (networkError) {
    // don't redirect if we're trying to switch/spoof user
    if (statusCode === 401 && !ignoredOperation(operation.operationName)) {
      if (window.location.pathname !== '/Login' && window.location.pathname !== '/Authenticate') {
        const queryParams = encodeURIComponent(window.location.search);
        window.location.href = `/Login?dest=${window.location.pathname}${queryParams}`;
      }
    } else if (statusCode === 403) {
      store.dispatch(
        serviceActions.genericAlert(
          'You do not have sufficient permission to access this resource',
          'Please contact your administrator',
          AlertType.Warning
        )
      );
    } else if (statusCode === 504) {
      store.dispatch(serviceActions.genericAlert('Request timed out', networkError.message, AlertType.Warning));
    } else if (statusCode === 503 || statusCode === 0) {
      window.location.href = `/maintenance.html?statusCode=${statusCode}`;
    }
  }
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path, errorType }) => {
      if (errorType !== 'CONFIRMATION_REQUIRED') {
        const error = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`;
        console.error(error);
      }
    });
  }
};

const cache = new InMemoryCache({
  addTypename: true,
  dataIdFromObject: (dataObject) => {
    //If the object is a mutation result...
    if (dataObject.clientMutationId) {
      const { clientMutationId, result, __typename } = dataObject;
      const { id, uuid } = result || {}; //Account for possibility that result is not defined (e.g. folderListUpdate).
      return id || uuid ? `${__typename}${id || uuid}` : `${__typename}${clientMutationId}`;
    }
    //If the object is an entity...
    if (dataObject.uniqueKey && dataObject[dataObject.uniqueKey]) {
      return `${dataObject.__typename}${dataObject.id || ''}${dataObject.concurrencyVersion || ''}${
        dataObject[dataObject.uniqueKey]
      }`;
    }
    return null; //Let apollo generate the key.
  },
  possibleTypes: introspectionToPossibleTypes(introspectionQueryResultData),
  typePolicies: {
    Query: {
      fields: buildTypePolicies(schema),
    },
  },
});

const timeStartLink = (_logoutTimer) =>
  new ApolloLink((operation, forward) => {
    _logoutTimer.resetTimer();
    return forward(operation);
  });

const DEVICE_ID_COOKIE = 'profit4deviceId';

const getDeviceId = () => {
  const cookies = new Cookies();

  const deviceId = cookies.get(DEVICE_ID_COOKIE) ?? uuIdGenerator();

  cookies.set(DEVICE_ID_COOKIE, deviceId, {
    path: '/',
    expires: add(new Date(), { days: 400 }), // 400 days is current max in Chrome
    secure: true,
  });

  return deviceId;
};

export const client = new ApolloClient({
  link: ApolloLink.from([
    timeStartLink(logoutTimer),
    onError(onErrorLink),
    new RetryLink({
      delay: {
        initial: 300,
        max: Infinity,
        jitter: true,
      },

      attempts: {
        max: 5,
        retryIf: (error, _operation) => {
          return error?.statusCode === 429 || error?.statusCode === 503;
        },
      },
    }),
    new HttpLink({
      uri: ({ operationName }) => `/api/graphql?op=${operationName}`,
      credentials: 'include',
      headers: {
        'Client-Width': window.innerWidth,
        'Client-Height': window.innerHeight,
        'Device-Id': getDeviceId(),
      },
    }),
  ]),
  cache,
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
    },
  },
});

client.onResetStore(() => {
  console.info('Apollo store reset');
});
